import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
  { value: 'note', label: 'Note' },
  { value: 'call', label: 'Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'email_logged', label: 'Email Logged' },
  { value: 'task', label: 'Task' },
];

const ActivityTimeline = ({ parentType, parentId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newAct, setNewAct] = useState({ type: 'note', subject: '', body: '' });

  const fetchAct = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/activities`, { params: { parent_type: parentType, parent_id: parentId } });
      setActivities(res.data.activities || []);
    } catch (err) { /* ignore */ }
    finally { setLoading(false); }
  }, [parentType, parentId]);

  useEffect(() => { fetchAct(); }, [fetchAct]);

  const addActivity = async (e) => {
    e.preventDefault();
    if (!newAct.body.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/activities`, {
        parent_type: parentType,
        parent_id: parentId,
        type: newAct.type,
        subject: newAct.subject || null,
        body: newAct.body,
      });
      setNewAct({ type: 'note', subject: '', body: '' });
      fetchAct();
      toast.success('Activity logged');
    } catch (err) { toast.error('Failed to log'); }
    finally { setAdding(false); }
  };

  return (
    <div className="border-t border-slate-800 pt-4" data-testid="activity-timeline">
      <Label className="text-slate-300 mb-2 block">Activity</Label>
      <form onSubmit={addActivity} className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-2 mb-3">
        <div className="flex gap-2">
          <Select value={newAct.type} onValueChange={v => setNewAct({ ...newAct, type: v })}>
            <SelectTrigger className="bg-slate-900 border-slate-800 w-[140px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              {ACTIVITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={newAct.subject} onChange={e => setNewAct({ ...newAct, subject: e.target.value })} placeholder="Subject (optional)" className="bg-slate-900 border-slate-800 h-9" />
        </div>
        <textarea
          value={newAct.body}
          onChange={e => setNewAct({ ...newAct, body: e.target.value })}
          rows={2}
          placeholder="What happened?"
          className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
          data-testid="activity-body"
        />
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={adding || !newAct.body.trim()} className="bg-violet-600 hover:bg-violet-500" data-testid="log-activity-btn">
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Log'}
          </Button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-600" /></div>
      ) : activities.length === 0 ? (
        <p className="text-xs text-slate-600 italic text-center py-4">No activity yet.</p>
      ) : (
        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
          {activities.map(a => (
            <div key={a.id} className="bg-slate-950 border border-slate-800 rounded-md p-3" data-testid={`activity-${a.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400 capitalize">{a.type.replace('_', ' ')}</Badge>
                  {a.subject && <span className="text-sm text-white">{a.subject}</span>}
                </div>
                <span className="text-[11px] text-slate-600">{new Date(a.occurred_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <p className="text-xs text-slate-300 whitespace-pre-wrap">{a.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityTimeline;
